import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AnalyticsSummaryCard from './components/AnalyticsSummaryCard';
import RatingDistributionChart from './components/RatingDistributionChart';
import ComplianceMetricsPanel from './components/ComplianceMetricsPanel';
import SentimentAnalysisChart from './components/SentimentAnalysisChart';
import ReviewsTable from './components/ReviewsTable';
import ReviewSubmissionModal from './components/ReviewSubmissionModal';
import { clientReviewService } from '../../services/clientReviewService';

const ReviewAnalyticsSuccessMetricsDashboard = () => {
  const [reviews, setReviews] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('all');
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);

  useEffect(() => {
    loadReviewData();
  }, []);

  const loadReviewData = async () => {
    try {
      setLoading(true);
      const [reviewsData, analyticsData] = await Promise.all([
        clientReviewService?.getAllPublishedReviews(),
        clientReviewService?.getReviewAnalytics()
      ]);
      
      setReviews(reviewsData);
      setAnalytics(analyticsData);
    } catch (err) {
      console.error('Error loading review data:', err);
      setError(err?.message || 'Failed to load review data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filters) => {
    // Filter changes handled by GlobalFilterBar component
  };

  const handleExport = () => {
    // Export functionality placeholder
    console.log('Exporting review analytics report...');
  };

  const handleReviewSubmitted = () => {
    loadReviewData(); // Reload data after new review
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <Header />
      <GlobalFilterBar onFilterChange={handleFilterChange} />
      <ComplianceAlertBanner />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl p-8 mb-8 text-white">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <Icon name="BarChart3" size={32} className="text-emerald-100" />
                <h1 className="text-3xl font-bold">Review Analytics & Success Metrics Dashboard</h1>
              </div>
              <p className="text-emerald-50 text-lg mb-6 max-w-3xl">
                Comprehensive insights into client feedback patterns, satisfaction trends, and quantified business impact for strategic decision-making.
              </p>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Icon name="Star" size={20} className="text-emerald-200" />
                  <span className="text-sm font-medium">{analytics?.averageRating || 0} Average Rating</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="MessageSquare" size={20} className="text-emerald-200" />
                  <span className="text-sm font-medium">{analytics?.totalReviews || 0} Total Reviews</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="TrendingUp" size={20} className="text-emerald-200" />
                  <span className="text-sm font-medium">{analytics?.satisfactionScore || 0}% Satisfaction</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="bg-white text-emerald-600 hover:bg-emerald-50 border-white"
                iconName="Plus"
                onClick={() => setShowSubmissionModal(true)}
              >
                Submit Review
              </Button>
              <Button
                variant="outline"
                className="bg-white text-emerald-600 hover:bg-emerald-50 border-white"
                iconName="Download"
                onClick={handleExport}
              >
                Export Report
              </Button>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400" />
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          </div>
        )}

        {/* Time Range Filter */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Icon name="Calendar" size={16} className="text-gray-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Time Range:</span>
            </div>
            <div className="flex items-center gap-2">
              {['all', '30days', '90days', '1year']?.map((range) => (
                <button
                  key={range}
                  onClick={() => setTimeRange(range)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    timeRange === range
                      ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400' :'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'
                  }`}
                >
                  {range === 'all' ? 'All Time' : range === '30days' ? 'Last 30 Days' : range === '90days' ? 'Last 90 Days' : 'Last Year'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Executive Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <AnalyticsSummaryCard
            title="Average Star Rating"
            value={analytics?.averageRating?.toFixed(1) || '0.0'}
            subtitle="Out of 5.0"
            icon="Star"
            iconColor="text-yellow-500"
            trend="up"
            trendValue="+0.3"
          />
          <AnalyticsSummaryCard
            title="Total Reviews Collected"
            value={analytics?.totalReviews || 0}
            subtitle={`${analytics?.verifiedReviews || 0} verified`}
            icon="MessageSquare"
            iconColor="text-blue-600"
            trend="up"
            trendValue="+12"
          />
          <AnalyticsSummaryCard
            title="Client Satisfaction Score"
            value={`${analytics?.satisfactionScore?.toFixed(1) || 0}%`}
            subtitle="4-5 star reviews"
            icon="ThumbsUp"
            iconColor="text-emerald-600"
            trend="up"
            trendValue="+5%"
          />
          <AnalyticsSummaryCard
            title="Aggregate Cost Savings"
            value={`K${(analytics?.totalCostSavingsKina || 0)?.toLocaleString()}`}
            subtitle="PNG Kina format"
            icon="DollarSign"
            iconColor="text-green-600"
            trend="up"
            trendValue="+K180K"
          />
        </div>

        {/* Main Analytics Panel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Sentiment Analysis */}
            <SentimentAnalysisChart reviews={reviews} />
            
            {/* Compliance Success Analytics */}
            <ComplianceMetricsPanel reviews={reviews} />
          </div>

          <div className="space-y-6">
            {/* Rating Distribution */}
            <RatingDistributionChart ratingDistribution={analytics?.ratingDistribution} />

            {/* Quick Stats */}
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="TrendingUp" size={20} className="text-emerald-600" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Impact Summary</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                  <div className="flex items-center gap-2">
                    <Icon name="Shield" size={16} className="text-emerald-600" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Violations Prevented</span>
                  </div>
                  <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                    {analytics?.totalViolationsPrevented || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircle2" size={16} className="text-blue-600" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Verified Reviews</span>
                  </div>
                  <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {analytics?.verifiedReviews || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Table */}
        <ReviewsTable reviews={reviews} loading={loading} />
      </main>

      {/* Review Submission Modal */}
      <ReviewSubmissionModal
        isOpen={showSubmissionModal}
        onClose={() => setShowSubmissionModal(false)}
        onSuccess={handleReviewSubmitted}
      />
    </div>
  );
};

export default ReviewAnalyticsSuccessMetricsDashboard;